import React, { useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
} from "@mui/material";
import { useSelector } from "react-redux";
import { Col, Row } from "reactstrap";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import moment from "moment";
import { withTranslation } from "react-i18next";
import * as Yup from "yup";

import {
  useLazyGetBillingCycleByIdQuery,
  usePostBillingCycleMutation,
} from "apis/BillingCycle";

import DateField from "Components/Common/DatePicker";
import InputFieldText from "Components/Common/InputFieldText";
import SelectField from "Components/Common/SelectField";
import {
  billingStatus,
  getOptionLabel,
  subscriptionTypes,
  getOptionValue,
} from "common/enums";
import { filterEmptyStrings, isAuthorizedUser } from "utils";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

const currentDate = moment();

const INITIATED_STATUS = "Initiated";

const closedBillingCycleStatus = getOptionValue("billingStatus", "Closed");

const statusFOrClosingBillCycle = [
  getOptionValue("billingStatus", "Executed"),
  getOptionValue("billingStatus", "Approved"),
  getOptionValue("billingStatus", "Dispatched"),
];

const validationSchema = Yup.object({
  subscriptionType: Yup.string().required("Subscription type is required"),
  // status: Yup.string().required("Status is required"),
  billCycle: Yup.date()
    .required("Bill cycle date is required")
    .test("isPastBillCycle", "Enter bill cycle date of past", (_, context) => {
      const [{ value: { billCycle } = {} } = {}] = context.from || [];
      return (
        billCycle?.isBefore(moment(), "year") ||
        (billCycle?.isSameOrBefore(moment(), "month") &&
          billCycle?.isSame(moment(), "year"))
      );
    }),
  chargeStartDate: Yup.date()
    .required("Charge start date is required")
    .test("isValidChargeStartDate", "Enter past date", (_, context) => {
      const [{ value: { chargeStartDate } = {} } = {}] = context.from || [];

      if (!chargeStartDate) {
        return context.createError({ message: "Enter a valid date" });
      }
      return chargeStartDate.isBefore(moment());
    }),
  chargeEndDate: Yup.date()
    .required("Charge end date is required")
    .test("isValidChargeEndDate", "", function () {
      const [{ value: { chargeEndDate, chargeStartDate } = null } = {}] =
        this.from || [];
      if (!chargeEndDate?.isValid()) {
        return this.createError({
          message: "Enter a valid date",
        });
      }
      if (chargeStartDate && chargeStartDate.isSameOrAfter(chargeEndDate)) {
        return this.createError({
          message: "Charge end date should be after charge start date",
        });
      }

      return true;
    }),
  validationDate: Yup.date()
    .required("Validation date is required")
    .test("isValidValidationDate", "", function () {
      const [{ value: { chargeEndDate, validationDate } = null } = {}] =
        this.from || [];
      if (!validationDate?.isValid()) {
        return this.createError({
          message: "Enter a valid date",
        });
      }
      if (chargeEndDate && chargeEndDate.isSameOrAfter(validationDate)) {
        return this.createError({
          message: "Validation date should be after charge end date",
        });
      }
      return true;
    }),

  billDate: Yup.date()
    .required("Bill date is required")
    .test("isValidBillDate", "", function () {
      const [{ value: { billDate, validationDate } = null } = {}] =
        this.from || [];
      if (!billDate?.isValid()) {
        return this.createError({
          message: "Enter a valid date",
        });
      }
      if (validationDate && validationDate.isSameOrAfter(billDate)) {
        return this.createError({
          message: "Bill date should be after validation date",
        });
      }
      return true;
    }),

  paymentDueDate: Yup.date()
    .required("payment due date is required")
    .test("isValidPaymentDueDate", "", function () {
      const [{ value: { billDate, paymentDueDate } = null } = {}] =
        this.from || [];
      if (!paymentDueDate?.isValid()) {
        return this.createError({
          message: "Enter a valid date",
        });
      }
      if (billDate && billDate.isSameOrAfter(paymentDueDate)) {
        return this.createError({
          message: "Payment due date should be after bill date",
        });
      }
      return true;
    }),

  paymentGraceDate: Yup.date()
    .required("Payment grace date is required")
    .test("isValidPaymentGraceDate", "", function () {
      const [{ value: { paymentGraceDate, paymentDueDate } = null } = {}] =
        this.from || [];
      if (!paymentGraceDate?.isValid()) {
        return this.createError({
          message: "Enter a valid date",
        });
      }
      if (paymentDueDate && paymentDueDate.isAfter(paymentGraceDate)) {
        return this.createError({
          message: "Payment grace date should be after payment due date",
        });
      }
      return true;
    }),
  reason: Yup.string(),
});

export const BillingCycleForm = ({
  selectedBillCycleId = "",
  handleClose = () => {},
  t,
}) => {
  const { roles } = useSelector((state) => state.Login);
  const [
    getBillingCycleData,
    {
      data: { data: billingCycleData } = {},
      isLoading: isBillingCycleDataLoading,
    },
  ] = useLazyGetBillingCycleByIdQuery();

  const [addBillingCycle, { isLoading: isBillingCycleAdding }] =
    usePostBillingCycleMutation();

  const isInitiatedStatus = useMemo(() =>
    billingCycleData?.status
      ? getOptionLabel("billingStatus", billingCycleData.status) ===
        INITIATED_STATUS
      : true
  );

  const updatedSubscriptionTypes = useMemo(
    () =>
      subscriptionTypes.filter(({ allowedRoles }) =>
        isAuthorizedUser(roles, allowedRoles)
      ),
    [roles, subscriptionTypes]
  );

  const initialValues = useMemo(
    () => ({
      id: billingCycleData?.id ?? 0,
      subscriptionType: billingCycleData?.subscriptionType ?? "",
      billCycle: billingCycleData?.billCycle
        ? convertUTCDateToLocalDate(billingCycleData.billCycle)
        : "",
      billGroup: "Default",
      status: billingCycleData?.status ?? billingStatus[0].value,
      chargeStartDate: billingCycleData?.chargeStartDate
        ? convertUTCDateToLocalDate(billingCycleData.chargeStartDate)
        : "",
      chargeEndDate: billingCycleData?.chargeEndDate
        ? convertUTCDateToLocalDate(billingCycleData.chargeEndDate)
        : "",
      billDate: billingCycleData?.billDate
        ? convertUTCDateToLocalDate(billingCycleData.billDate)
        : "",
      validationDate: billingCycleData?.validationDate
        ? convertUTCDateToLocalDate(billingCycleData.validationDate)
        : "",
      paymentDueDate: billingCycleData?.paymentDueDate
        ? convertUTCDateToLocalDate(billingCycleData.paymentDueDate)
        : "",
      paymentGraceDate: billingCycleData?.paymentGraceDate
        ? convertUTCDateToLocalDate(billingCycleData.paymentGraceDate)
        : "",
      reason: billingCycleData?.reason ?? "",
    }),
    [billingCycleData]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: validationSchema,

    onSubmit: async (values, { resetForm }) => {
      const {
        billCycle,
        chargeEndDate,
        chargeStartDate,
        billDate,
        validationDate,
        paymentDueDate,
        paymentGraceDate,
      } = values;
      try {
        const payload = {
          ...values,
          billCycle: billCycle.format("YYYY-MM-DD"),
          chargeStartDate: chargeStartDate.format("YYYY-MM-DD"),
          chargeEndDate: chargeEndDate.format("YYYY-MM-DD"),
          billDate: billDate.format("YYYY-MM-DD"),
          validationDate: validationDate.format("YYYY-MM-DD"),
          paymentDueDate: paymentDueDate.format("YYYY-MM-DD"),
          paymentGraceDate: paymentGraceDate.format("YYYY-MM-DD"),
        };
        const billingCyclePayload = filterEmptyStrings(payload);
        await addBillingCycle(billingCyclePayload);
        handleClose();
        resetForm();
      } catch (error) {}
    },
  });

  const {
    values: {
      chargeStartDate,
      chargeEndDate,
      billDate,
      status,
      validationDate,
      paymentDueDate,
      subscriptionType,
    },
    handleSubmit,
    setFieldValue,
  } = formik;

  const isPowerUtility = useMemo(
    () =>
      [
        getOptionValue("subscriptionTypes", "Electricity"),
        getOptionValue("subscriptionTypes", "Security Deposit"),
        getOptionValue("subscriptionTypes", "Electricity"),
      ].includes(subscriptionType),
    [subscriptionType]
  );

  const getBillingCycleDetails = async (billingCycleId) => {
    try {
      await getBillingCycleData(billingCycleId);
    } catch (err) {}
  };

  useEffect(() => {
    if (selectedBillCycleId) {
      getBillingCycleDetails(selectedBillCycleId);
    }
  }, [selectedBillCycleId]);

  if (isBillingCycleDataLoading) {
    return (
      <div className="d-flex justify-content-center mt-3">
        <CircularProgress />;
      </div>
    );
  }

  return (
    <Row className="mt-3">
      <Col xs={12} sm={4}>
        <SelectField
          label={t("Subscription Type") + "*"}
          name="subscriptionType"
          formik={formik}
          options={updatedSubscriptionTypes || []}
        />
      </Col>
      <Col xs={12} sm={4}>
        <InputFieldText
          label={t("Bill Group")}
          name="billGroup"
          formik={formik}
          disabled
          placeholder="Enter Unit Name"
        />
      </Col>
      {/* <Col xs={12} sm={4}>
        <SelectField
          label={t("Status") + "*"}
          name="status"
          formik={formik}
          options={billingStatus || []}
          getOptionLabel={(option) => option.label}
          getOptionValue={(option) => option.value}
        />
      </Col> */}
      <Col xs={12} sm={4}>
        <DateField
          label={t("Bill Cycle") + "*"}
          name="billCycle"
          maxDate={moment()}
          views={["month", "year"]}
          formik={formik}
        />
      </Col>
      <Col xs={12} sm={4}>
        <DateField
          label={t("Charge Start Date") + "*"}
          name="chargeStartDate"
          isDisable={!isInitiatedStatus}
          formik={formik}
          maxDate={currentDate}
          format="DD-MM-YYYY"
        />
      </Col>
      <Col xs={12} sm={4}>
        <DateField
          label={t("Charge End Date") + "*"}
          name="chargeEndDate"
          isDisable={!isInitiatedStatus}
          minDate={moment(chargeStartDate)}
          maxDate={currentDate}
          formik={formik}
          format="DD-MM-YYYY"
        />
      </Col>
      <Col xs={12} sm={4}>
        <DateField
          label={t("Validation Date") + "*"}
          name="validationDate"
          minDate={moment(chargeEndDate).add(1, "days")}
          formik={formik}
          format="DD-MM-YYYY"
        />
      </Col>
      <Col xs={12} sm={4}>
        <DateField
          label={t("Bill Date") + "*"}
          name="billDate"
          onChange={(value) => {
            if (value?.isValid()) {
              setFieldValue("billDate", value);
              if (!isPowerUtility) {
                setFieldValue("paymentDueDate", moment(value).add(15, "days"));
              }
            } else {
              setFieldValue("billDate", "");
              setFieldValue("paymentDueDate", "");
            }
          }}
          format="DD-MM-YYYY"
          formik={formik}
          minDate={moment(validationDate).add(1, "days")}
        />
      </Col>
      <Col xs={12} sm={4}>
        <DateField
          label={t("Payment Due Date") + "*"}
          name="paymentDueDate"
          format="DD-MM-YYYY"
          disabled={!isPowerUtility}
          minDate={moment(billDate).add(1, "days")}
          formik={formik}
        />
      </Col>
      <Col xs={12} sm={4}>
        <DateField
          label={t("Payment Grace Date") + "*"}
          name="paymentGraceDate"
          format="DD-MM-YYYY"
          minDate={moment(paymentDueDate)}
          formik={formik}
        />
      </Col>

      <Col xs={12}>
        <InputFieldText
          label={t("Reason")}
          name="reason"
          formik={formik}
          multiline
          minRows={2}
          maxRows={6}
          placeholder="Enter reason"
        />
      </Col>
      {!!selectedBillCycleId &&
        statusFOrClosingBillCycle.includes(billingCycleData?.status) && (
          <Col md={4} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={status === closedBillingCycleStatus}
                  onChange={(_, isChecked) => {
                    setFieldValue(
                      "status",
                      isChecked
                        ? closedBillingCycleStatus
                        : initialValues.status
                    );
                  }}
                />
              }
              label={t("Closed")}
            />
          </Col>
        )}
      <Box className="d-flex justify-content-end" mt={2}>
        <Button
          variant="contained"
          size="medium"
          type="submit"
          onClick={handleSubmit}
          disabled={isBillingCycleAdding}
        >
          {selectedBillCycleId ? t("Save") : t("Submit")}
          {isBillingCycleAdding && <CircularProgress size={20} />}
        </Button>
      </Box>
    </Row>
  );
};

BillingCycleForm.propTypes = {
  selectedBillCycleId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  handleClose: PropTypes.func,
  t: PropTypes.func,
};

export default withTranslation()(BillingCycleForm);
